/** Default ECharts palette when no semantic match. */
export const DEFAULT_CHART_PALETTE = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272',
    '#fc8452', '#9a60b4', '#ea7ccc',
];

/** RAG data quality (DataQualityStatus). */
const DATA_QUALITY = {
    green: '#52c41a',
    valid: '#52c41a',
    amber: '#faad14',
    yellow: '#faad14',
    red: '#ff4d4f',
    invalid: '#ff4d4f',
    '(not set)': '#bfbfbf',
    'not stated': '#bfbfbf',
    unknown: '#bfbfbf',
};

/** Record processing / pipeline status. */
const PROCESSING_STATUS = {
    processed: '#52c41a',
    complete: '#52c41a',
    completed: '#52c41a',
    inserted: '#52c41a',
    pending: '#faad14',
    failed: '#ff4d4f',
    error: '#ff4d4f',
    skipped: '#d9d9d9',
    duplicates: '#faad14',
    'duplicates skipped': '#faad14',
};

/** OMOP import status (cosd and other vault tables). */
const OMOP_IMPORT = {
    pending: '#faad14',
    imported: '#52c41a',
    complete: '#52c41a',
    completed: '#52c41a',
    failed: '#ff4d4f',
    skipped: '#d9d9d9',
};

const SCHEMES = {
    data_quality: DATA_QUALITY,
    processing: PROCESSING_STATUS,
    omop: OMOP_IMPORT,
};

const NHS_VALID_GREEN = '#52c41a';
const NHS_INVALID_RED = '#ff4d4f';
const NHS_WARNING_AMBER = '#faad14';

function norm(s) {
    return String(s ?? '').trim().toLowerCase();
}

function lookup(map, label) {
    const k = norm(label);
    if (!k) return undefined;
    if (map[k] !== undefined) return map[k];
    for (const [key, color] of Object.entries(map)) {
        if (k.includes(key) || key.includes(k)) return color;
    }
    return undefined;
}

/** NHS validation: valid outcomes green; invalid red; other states amber. */
function resolveNhsValidationColor(label) {
    const k = norm(label);
    if (k.includes('invalid')) return NHS_INVALID_RED;
    if (k === 'valid' || k.includes('nhs valid')) return NHS_VALID_GREEN;
    return NHS_WARNING_AMBER;
}

/** NHS-related data quality reasons: any NHS-valid outcome is green. */
function resolveDataQualityReasonColor(label) {
    const k = norm(label);
    if (k.includes('invalid') || k.includes('missing critical')) return NHS_INVALID_RED;
    if (k.includes('nhs valid')) return NHS_VALID_GREEN;
    if (k.includes('not set') || k === 'unknown') return NHS_WARNING_AMBER;
    if (k.includes('nhs')) return NHS_WARNING_AMBER;
    return undefined;
}

/**
 * @param {string} label - category name from query
 * @param {string} [colorScheme] - data_quality | nhs_validation | data_quality_reason | processing | omop
 */
export function resolveCategoryColor(label, colorScheme) {
    if (colorScheme === 'nhs_validation') {
        return resolveNhsValidationColor(label);
    }
    if (colorScheme === 'data_quality_reason') {
        return resolveDataQualityReasonColor(label) ?? NHS_WARNING_AMBER;
    }
    if (colorScheme && SCHEMES[colorScheme]) {
        return lookup(SCHEMES[colorScheme], label);
    }
    return (
        lookup(DATA_QUALITY, label)
        ?? lookup(PROCESSING_STATUS, label)
        ?? lookup(OMOP_IMPORT, label)
    );
}

/**
 * Build per-row colors for chart series (pie slices or bar items).
 * @param {object[]} rows
 * @param {string} catCol
 * @param {string} [colorScheme]
 */
export function colorsForRows(rows, catCol, colorScheme) {
    const palette = DEFAULT_CHART_PALETTE;
    return rows.map((row, i) => {
        const label = row[catCol];
        return resolveCategoryColor(label, colorScheme) ?? palette[i % palette.length];
    });
}
